import React from 'react';

// Display-only formatter for free text typed by the team (manuales).
// The raw text is never rewritten — this only decides how each line looks:
//   ☐ / □ / [ ]      → checklist item
//   ☑ / ✓ / ✔ / [x]  → checked checklist item
//   - / •            → bullet
//   "Algo:" followed by list items → subheading
//   blank line       → paragraph break
//   **bold**, *italic* inline

const WINE = '#6B1C2A';

const RE_UNCHECKED = /^\s*(?:☐|□|\[ \])\s*(.*)$/;
const RE_CHECKED = /^\s*(?:☑|✓|✔|☒|\[[xX]\])\s*(.*)$/;
const RE_BULLET = /^\s*(?:-\s+|•\s*)(.*)$/;
const RE_INLINE = /(\*\*[^*\n]+?\*\*|\*[^*\s\n][^*\n]*?\*)/g;

function classifyLine(line) {
  let m = line.match(RE_UNCHECKED);
  if (m) return { kind: 'unchecked', text: m[1] };
  m = line.match(RE_CHECKED);
  if (m) return { kind: 'checked', text: m[1] };
  m = line.match(RE_BULLET);
  if (m) return { kind: 'bullet', text: m[1] };
  if (!line.trim()) return { kind: 'blank', text: '' };
  return { kind: 'text', text: line };
}

const isListKind = k => k === 'unchecked' || k === 'checked' || k === 'bullet';

// Lines → blocks: paragraph (consecutive text lines), list, heading, gap.
export function parseBlocks(text) {
  const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n').map(classifyLine);
  const blocks = [];
  lines.forEach((ln, i) => {
    const last = blocks[blocks.length - 1];
    if (ln.kind === 'blank') {
      if (last && last.type !== 'gap') blocks.push({ type: 'gap' });
      return;
    }
    if (isListKind(ln.kind)) {
      if (last && last.type === 'list') last.items.push(ln);
      else blocks.push({ type: 'list', items: [ln] });
      return;
    }
    const next = lines[i + 1];
    if (ln.text.trim().endsWith(':') && next && isListKind(next.kind)) {
      blocks.push({ type: 'heading', text: ln.text.trim() });
      return;
    }
    if (last && last.type === 'p') last.lines.push(ln.text);
    else blocks.push({ type: 'p', lines: [ln.text] });
  });
  while (blocks.length && blocks[blocks.length - 1].type === 'gap') blocks.pop();
  return blocks;
}

export function renderInline(text) {
  return String(text).split(RE_INLINE).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      // eslint-disable-next-line react/no-array-index-key
      return <strong key={i} style={{ fontWeight: 600 }}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      // eslint-disable-next-line react/no-array-index-key
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    return part;
  });
}

// Cut long text for a collapsed preview, preferring a line break, then a
// word boundary, so list markers and **bold** pairs aren't split mid-way.
export function truncateText(text, max) {
  const t = String(text || '');
  if (t.length <= max) return t;
  const lastBreak = t.lastIndexOf('\n', max);
  if (lastBreak >= max * 0.4) return `${t.slice(0, lastBreak).trimEnd()}\n…`;
  const lastSpace = t.lastIndexOf(' ', max);
  return `${t.slice(0, lastSpace > max * 0.6 ? lastSpace : max).trimEnd()}…`;
}

export default function RichText({ text, color = '#3A3A3A', fontSize = 15, lineHeight = 1.7, maxWidth = '70ch' }) {
  const blocks = parseBlocks(text);
  if (!blocks.length) return null;
  const base = {
    fontFamily: "'DM Sans',sans-serif", fontSize, color, lineHeight,
    maxWidth, margin: 0, overflowWrap: 'anywhere',
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {blocks.map((b, i) => {
        const key = `${b.type}-${i}`;
        if (b.type === 'gap') return <div key={key} style={{ height: 12 }} aria-hidden="true" />;
        if (b.type === 'heading') {
          return (
            <p key={key} style={{ ...base, fontWeight: 600, color: WINE, marginTop: i === 0 ? 0 : 12, marginBottom: 4 }}>
              {renderInline(b.text)}
            </p>
          );
        }
        if (b.type === 'list') {
          return (
            <ul key={key} style={{ ...base, listStyle: 'none', padding: '0 0 0 8px', margin: '4px 0', display: 'flex', flexDirection: 'column', gap: 6, lineHeight: 1.5 }}>
              {b.items.map((it, j) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  {it.kind === 'bullet' ? (
                    <span aria-hidden="true" style={{ ...st.bullet, marginTop: (fontSize * 1.5) / 2 - 3 }} />
                  ) : (
                    <span
                      role="img"
                      aria-label={it.kind === 'checked' ? 'Hecho' : 'Pendiente'}
                      style={{ ...st.box, marginTop: (fontSize * 1.5 - 16) / 2 }}
                    >
                      {it.kind === 'checked' && <span style={st.check}>✓</span>}
                    </span>
                  )}
                  <span style={{ whiteSpace: 'pre-wrap', minWidth: 0, ...(it.kind === 'checked' ? { color: '#9A9590' } : {}) }}>
                    {renderInline(it.text)}
                  </span>
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={key} style={{ ...base, whiteSpace: 'pre-wrap' }}>
            {b.lines.map((ln, j) => (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={j}>
                {j > 0 && '\n'}
                {renderInline(ln)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}

const st = {
  box: {
    width: 16, height: 16, flexShrink: 0, boxSizing: 'border-box',
    border: `1.5px solid ${WINE}`, borderRadius: 3, background: '#fff',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  },
  check: { color: WINE, fontSize: 12, fontWeight: 700, lineHeight: 1 },
  bullet: { width: 6, height: 6, borderRadius: '50%', background: WINE, flexShrink: 0 },
};
